/**
 * Wat een AI (MCP-koppeling) in een workpackage mag schrijven — migratie 0062.
 *
 * Gedeeld door Instellingen → Workpackages (compact, per rij) en de
 * Workpackage-instellingen in het Repository-menu (volledig, met uitleg). De
 * server dwingt de stand af (`apps/worker/src/middleware/aiWriteGuard.ts`);
 * wijzigen kan alleen vanuit de browser.
 */
import { useTranslation } from "react-i18next";
import type { AiWriteMode } from "../api/client";

export const AI_WRITE_OPTIONS: readonly AiWriteMode[] = ["none", "ai_plateau", "anywhere"];

/** Compacte keuzelijst, voor in een tabelrij. */
export function AiWriteSelect({
  value,
  onChange,
  disabled,
}: {
  value: AiWriteMode;
  onChange: (next: AiWriteMode) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex items-center gap-2 text-(--ld-muted) text-xs">
      <span>{t("aiWrite.label")}</span>
      <select
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 text-xs"
        value={value}
        disabled={disabled}
        title={t(`aiWrite.help.${value}`)}
        onChange={(e) => onChange(e.target.value as AiWriteMode)}
      >
        {AI_WRITE_OPTIONS.map((m) => (
          <option key={m} value={m}>
            {t(`aiWrite.${m}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Keuzerondjes met uitleg per stand, voor de instellingenpagina. */
export function AiWriteChoice({
  value,
  onChange,
  readOnly,
}: {
  value: AiWriteMode;
  onChange: (next: AiWriteMode) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-2 text-sm">
      <legend className="mb-1 font-medium text-(--ld-text)">{t("aiWrite.label")}</legend>
      {AI_WRITE_OPTIONS.map((m) => (
        <label
          key={m}
          className={`flex items-start gap-2 rounded border px-3 py-2 ${
            value === m
              ? "border-(--ld-accent) bg-(--ld-active)"
              : "border-(--ld-border) bg-(--ld-surface)"
          } ${readOnly ? "cursor-default" : "cursor-pointer"}`}
        >
          <input
            type="radio"
            name="ai-write"
            className="mt-1"
            checked={value === m}
            disabled={readOnly}
            onChange={() => onChange(m)}
          />
          <span>
            <span className="font-medium text-(--ld-text)">{t(`aiWrite.${m}`)}</span>
            <span className="block text-(--ld-muted) text-xs">{t(`aiWrite.help.${m}`)}</span>
          </span>
        </label>
      ))}
      <p className="text-(--ld-muted) text-xs">{t("aiWrite.note")}</p>
    </fieldset>
  );
}
